/* eslint-disable no-underscore-dangle, max-len, no-await-in-loop, new-cap, no-use-before-define */
/**
 * Weir Flow Capacity Chart PDF Export
 *
 * Client-side vector PDF generation using jsPDF (2.5.1) and svg2pdf.js (2.2.1).
 * Features:
 * - Lazy-loaded UMD bundles on first click.
 * - Accurate US Letter Landscape layout (792x612pt) with header logos, vector chart, and disclaimer.
 * - Accessible state transitions and mobile bandwidth warning dialog.
 */

let _loadPromise = null;
const buttonResetTimers = new WeakMap();

export const DISCLAIMER_PARAS = [
    'DISCLAIMER: This Flow Capacity Chart for sag conditions is provided for informational purposes only, and all data and results are provided “as is.”',
    'Although Neenah Foundry Company makes reasonable efforts to ensure accuracy, it does not warrant or guarantee the completeness or applicability of the information and assumes no responsibility for errors, omissions, or outcomes resulting from its use.',
    'This tool does not constitute engineering or design advice. Results depend on user-supplied inputs and assumptions, which are not verified by Neenah Foundry. A qualified design professional must review and confirm all information and is solely responsible for determining its suitability for specific project conditions, governing codes, and regulatory requirements.',
    'Neenah Foundry Company is not liable for any direct or indirect damages arising from the use or misuse of this tool.',
    'For sheet flow conditions, refer to the Federal Highway Hydraulic Toolbox.',
];

if (typeof window !== 'undefined') {
    window.nfcDisclaimerParas = DISCLAIMER_PARAS;
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
    });
}

export async function ensurePdfLibs(config = {}) {
    if ((window.jspdf && window.jspdf.jsPDF) && (window.svg2pdf && window.svg2pdf.svg2pdf)) {
        return { jsPDF: window.jspdf.jsPDF, svg2pdf: window.svg2pdf.svg2pdf };
    }

    if (_loadPromise) {
        return _loadPromise;
    }

    const jspdfUrl = (config.pdfLibUrls && config.pdfLibUrls.jspdf) || '/scripts/vendor/jspdf.umd.min.js';
    const svg2pdfUrl = (config.pdfLibUrls && config.pdfLibUrls.svg2pdf) || '/scripts/vendor/svg2pdf.umd.min.js';

    _loadPromise = (async () => {
        try {
            if (!(window.jspdf && window.jspdf.jsPDF)) {
                await loadScript(jspdfUrl);
            }
            if (!(window.svg2pdf && window.svg2pdf.svg2pdf)) {
                await loadScript(svg2pdfUrl);
            }
            if (!(window.jspdf && window.jspdf.jsPDF) || !(window.svg2pdf && window.svg2pdf.svg2pdf)) {
                throw new Error('PDF libraries failed to initialize.');
            }
            return { jsPDF: window.jspdf.jsPDF, svg2pdf: window.svg2pdf.svg2pdf };
        } catch (err) {
            _loadPromise = null; // Fix 4: reset on failure so retry works
            throw err;
        }
    })();

    return _loadPromise;
}

export function announce(msg) {
    let el = document.getElementById('nf-a11y-announce');
    if (!el) {
        el = document.createElement('div');
        el.id = 'nf-a11y-announce';
        el.setAttribute('aria-live', 'polite');
        el.className = 'sr-only screen-reader-text';
        el.style.cssText = 'position:absolute;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;';
        document.body.appendChild(el);
    }
    el.textContent = '';
    requestAnimationFrame(() => {
        el.textContent = msg;
    });
}

function isIosSafari() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
    return isIos && isSafari;
}

export function showToast(msg) {
    let toast = document.querySelector('.wfc__toast');
    if (!toast) {
        toast = document.createElement('div');
        toast.className = 'wfc__toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('wfc__toast--visible');

    if (toast._timer) clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
        toast.classList.remove('wfc__toast--visible');
    }, 6000);
}

export function showBandwidthWarning() {
    return new Promise(resolve => {
        const existing = document.querySelector('.wfc__bandwidth-warning');
        if (existing) existing.remove();

        const dialog = document.createElement('div');
        dialog.className = 'wfc__bandwidth-warning';
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
        dialog.setAttribute('aria-labelledby', 'wfc-bw-title');

        dialog.innerHTML = `
            <div class="wfc__bandwidth-warning-box">
                <p id="wfc-bw-title">Generating PDF loads ≈140 KB of scripts.</p>
                <div class="wfc__bandwidth-warning-actions">
                    <button type="button" class="wfc__bw-confirm">Generate PDF</button>
                    <button type="button" class="wfc__bw-cancel">Cancel</button>
                </div>
            </div>
        `;

        const confirmBtn = dialog.querySelector('.wfc__bw-confirm');
        const cancelBtn = dialog.querySelector('.wfc__bw-cancel');

        function cleanup(confirmed) {
            window.removeEventListener('keydown', handleKey);
            dialog.remove();
            resolve(confirmed);
        }

        function handleKey(e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                cleanup(false);
            } else if (e.key === 'Tab') {
                if (e.shiftKey && document.activeElement === confirmBtn) {
                    e.preventDefault();
                    cancelBtn.focus();
                } else if (!e.shiftKey && document.activeElement === cancelBtn) {
                    e.preventDefault();
                    confirmBtn.focus();
                }
            }
        }

        confirmBtn.addEventListener('click', () => cleanup(true));
        cancelBtn.addEventListener('click', () => cleanup(false));
        window.addEventListener('keydown', handleKey);

        document.body.appendChild(dialog);
        confirmBtn.focus();
    });
}

function fetchLogoDataUrl(url, targetWidth, targetHeight) {
    if (!url) return Promise.resolve(null);
    return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const scale = 2;
                canvas.width = targetWidth * scale;
                canvas.height = targetHeight * scale;
                const ctx = canvas.getContext('2d');

                const naturalW = img.naturalWidth || img.width || targetWidth;
                const naturalH = img.naturalHeight || img.height || targetHeight;
                const imgRatio = naturalW / naturalH;
                const targetRatio = targetWidth / targetHeight;

                let drawW = targetWidth * scale;
                let drawH = targetHeight * scale;
                let drawX = 0;
                let drawY = 0;

                if (imgRatio > targetRatio) {
                    drawH = drawW / imgRatio;
                    drawY = (targetHeight * scale - drawH) / 2;
                } else {
                    drawW = drawH * imgRatio;
                    drawX = (targetWidth * scale - drawW) / 2;
                }

                ctx.drawImage(img, drawX, drawY, drawW, drawH);
                resolve(canvas.toDataURL('image/png'));
            } catch (err) {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

export function formatGrateType(grate) {
    if (!grate) return '';
    const trimmed = String(grate).trim();
    if (trimmed.length === 1) {
        return trimmed.toUpperCase();
    }
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

export function getFilename(catalog, grate) {
    const cat = (catalog ? String(catalog).trim().toUpperCase() : '') || 'UNKNOWN';
    const gr = (grate ? String(grate).trim().toUpperCase() : '') || 'UNKNOWN';

    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const ymd = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const hm = `${pad(now.getHours())}${pad(now.getMinutes())}`;

    return `CPFI-NFC_FlowCapacityChart_${cat}-GRATE${gr}_${ymd}-${hm}.pdf`;
}

function yieldToMain() {
    if ('scheduler' in window && typeof window.scheduler.yield === 'function') {
        return window.scheduler.yield();
    }
    return new Promise(resolve => setTimeout(resolve, 0));
}

function getSvgClone(container) {
    if (!container) return null;

    if (container._wfcInstance && typeof container._wfcInstance.exportSVG === 'function') {
        return container._wfcInstance.exportSVG();
    }

    const svg = container.querySelector('svg');
    if (svg) {
        const clone = svg.cloneNode(true);
        clone.querySelectorAll('.wfc__line, line, polyline').forEach(el => {
            if (el.style.opacity === '0') el.style.opacity = '1';
        });
        return clone;
    }

    return null;
}

export async function generateWeirPdf({ container, catalog, grate, config = {} }) {
    const { jsPDF, svg2pdf } = await ensurePdfLibs(config);

    // Wait up to 10s for SVG clone
    let svgClone = getSvgClone(container);
    if (!svgClone) {
        const start = Date.now();
        while (!svgClone && Date.now() - start < 10000) {
            await new Promise(r => setTimeout(r, 100));
            svgClone = getSvgClone(container);
        }
    }
    if (!svgClone) {
        throw new Error('Chart did not finish rendering within 10 s.');
    }

    const catalogNumber = catalog
        || ((container._wfcInstance && container._wfcInstance.getCatalogNumber) ? container._wfcInstance.getCatalogNumber() : '')
        || config.catalogNumber
        || '';
    const grateType = grate
        || ((container._wfcInstance && container._wfcInstance.getCurrentGrate) ? container._wfcInstance.getCurrentGrate() : '')
        || config.grateType
        || '';

    // Fetch logos in parallel
    const cpfLogoUrl = (config.logoUrls && config.logoUrls.cpf) || '/images/weir-pdf/CPF-Infrastructure.png';
    const nfLogoUrl = (config.logoUrls && config.logoUrls.nf) || '/images/weir-pdf/logo-NeenahFoundry.svg';
    const [cpfDataUrl, nfDataUrl] = await Promise.all([
        fetchLogoDataUrl(cpfLogoUrl, 125, 40),
        fetchLogoDataUrl(nfLogoUrl, 90, 40),
    ]);

    // Dimensions: US Letter Landscape (792 x 612 pt)
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });

    // 1. Header background (y=0 to y=60)
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, 792, 60, 'F');

    // Logos on left
    if (cpfDataUrl) {
        pdf.addImage(cpfDataUrl, 'PNG', 12, 10, 125, 40);
    }
    if (nfDataUrl) {
        pdf.addImage(nfDataUrl, 'PNG', 147, 10, 90, 40);
    }

    // Title centered
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(4, 30, 66);
    pdf.text('Flow Capacity Chart', 396, 36, { align: 'center' });

    // Catalog & Grate lines on right
    if (catalogNumber) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(4, 30, 66);
        pdf.text(catalogNumber, 780, 20, { align: 'right' });
    }
    if (grateType) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(4, 30, 66);
        pdf.text('Grate Type: ' + formatGrateType(grateType), 780, 30, { align: 'right' });
    }

    // 2. Chart SVG (y=60 to y=532)
    svgClone.style.cssText = 'position:absolute;left:-9999px;top:0;width:792px;height:472px;visibility:visible;';
    document.body.appendChild(svgClone);

    await yieldToMain();
    try {
        await svg2pdf(svgClone, pdf, {
            x: 0,
            y: 60,
            width: 792,
            height: 472,
        });
    } finally {
        if (svgClone.parentNode) {
            svgClone.parentNode.removeChild(svgClone);
        }
    }
    await yieldToMain();

    // 3. Footer (y=532 to y=612)
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 532, 792, 80, 'F');

    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.5);
    pdf.line(12, 532, 780, 532);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(5.5);
    pdf.setTextColor(90, 90, 90);

    let cursorY = 540;
    DISCLAIMER_PARAS.forEach(para => {
        const lines = pdf.splitTextToSize(para, 633.6);
        pdf.text(lines, 12, cursorY);
        cursorY += lines.length * 7.975 + 2;
    });

    // 4. Deliver file
    const filename = getFilename(catalogNumber, grateType);
    pdf.save(filename);

    announce('PDF download started. Check your downloads folder.');

    if (isIosSafari()) {
        showToast('Tap the share icon (↑) → Save to Files to keep your PDF.');
    }
}

export async function handleExportClick(btn) {
    if (btn.dataset.pdfBusy === 'true') {
        return;
    }

    // Locate chart container
    let container = null;
    if (btn.dataset.chartId) {
        container = document.getElementById(btn.dataset.chartId);
    }
    if (!container) {
        container = (btn.closest('.infrastructure-product-dialog__panel') ? btn.closest('.infrastructure-product-dialog__panel').querySelector('.weir-flow-chart') : null)
            || btn.closest('.wfc-section__chart-wrapper')
            || document.querySelector('.weir-flow-chart');
    }
    if (!container) {
        console.warn('[weir-pdf-export] No chart container found.');
        return;
    }

    // Bandwidth warning on mobile viewports
    if (window.innerWidth <= 768 && !sessionStorage.getItem('wfc-pdf-warned')) {
        const confirmed = await showBandwidthWarning();
        if (!confirmed) {
            return;
        }
        sessionStorage.setItem('wfc-pdf-warned', '1');
    }

    btn.dataset.pdfBusy = 'true';
    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('wfc__export-btn--loading');

    const labelEl = btn.querySelector('.wfc__export-btn-label') || btn.querySelector('span');
    const originalLabel = labelEl ? labelEl.textContent : 'DOWNLOAD';
    if (labelEl) labelEl.textContent = 'Generating PDF…';
    btn.setAttribute('aria-label', 'Generating PDF, please wait');

    try {
        const catalog = btn.dataset.catalogNumber
            || ((container._wfcInstance && container._wfcInstance.getCatalogNumber) ? container._wfcInstance.getCatalogNumber() : '')
            || '';
        const grate = btn.dataset.grateType
            || ((container._wfcInstance && container._wfcInstance.getCurrentGrate) ? container._wfcInstance.getCurrentGrate() : '')
            || '';

        await generateWeirPdf({
            container,
            catalog,
            grate,
            config: window.nfcWeirConfig || {},
        });

        btn.classList.remove('wfc__export-btn--loading');
        btn.classList.add('wfc__export-btn--success');
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
        if (labelEl) labelEl.textContent = 'PDF Downloaded';
        btn.setAttribute('aria-label', 'PDF downloaded. Click to download again.');

        // GA4 tracking
        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'flow_chart_pdf_download',
                catalog_number: catalog,
                grate_type: grate,
            });
        }

        if (buttonResetTimers.has(btn)) {
            clearTimeout(buttonResetTimers.get(btn));
        }
        const timer = setTimeout(() => {
            btn.classList.remove('wfc__export-btn--success');
            if (labelEl) labelEl.textContent = originalLabel;
            btn.setAttribute('aria-label', 'Download Flow Capacity Chart as PDF');
            btn.removeAttribute('data-pdf-busy');
        }, 2500);
        buttonResetTimers.set(btn, timer);

    } catch (err) {
        console.error('[weir-pdf-export] PDF generation failed:', err);
        btn.classList.remove('wfc__export-btn--loading');
        btn.classList.add('wfc__export-btn--error');
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
        if (labelEl) labelEl.textContent = 'PDF generation failed. Try again.';
        btn.setAttribute('aria-label', 'PDF generation failed. Click to try again.');
        announce('PDF generation failed. Please try again.');
        btn.focus();

        setTimeout(() => {
            btn.classList.remove('wfc__export-btn--error');
            if (labelEl) labelEl.textContent = originalLabel;
            btn.setAttribute('aria-label', 'Download Flow Capacity Chart as PDF');
            btn.removeAttribute('data-pdf-busy');
        }, 3000);
    } finally {
        delete btn.dataset.pdfBusy;
    }
}

export function initWeirPdfExport() {
    // Delegated click handler on document for export buttons
    document.addEventListener('click', e => {
        const btn = e.target.closest('.wfc__export-btn, [data-wfc-export]');
        if (btn) {
            e.preventDefault();
            handleExportClick(btn);
        }
    });

    // Keyboard support (Enter/Space)
    document.addEventListener('keydown', e => {
        const btn = e.target.closest('.wfc__export-btn, [data-wfc-export]');
        if (btn && (e.key === ' ' || e.key === 'Spacebar')) {
            e.preventDefault();
            handleExportClick(btn);
        }
    });

    // Entry Point B: CustomEvent listener
    const runningDownloads = new Set();
    document.addEventListener('nfc:flow-capacity:download', async e => {
        const { productId, catalog, grateSlug } = e.detail || {};
        if (!productId || runningDownloads.has(productId)) {
            return;
        }

        runningDownloads.add(productId);
        let error = false;
        try {
            const chartArea = document.getElementById(`chart-area-${productId}`);
            const svgEl = (chartArea ? chartArea.querySelector('.chart-canvas svg') : null);
            if (!svgEl) {
                throw new Error('Chart SVG not found for inline download.');
            }

            const container = {
                _wfcInstance: {
                    exportSVG: () => {
                        const clone = svgEl.cloneNode(true);
                        clone.querySelectorAll('.wfc__line, line, polyline').forEach(el => {
                            if (el.style.opacity === '0') el.style.opacity = '1';
                        });
                        return clone;
                    },
                    getCurrentGrate: () => grateSlug || '',
                    getCatalogNumber: () => catalog || '',
                },
            };

            await generateWeirPdf({
                container,
                catalog,
                grate: grateSlug,
                config: window.nfcWeirConfig || {},
            });
        } catch (err) {
            console.error('[weir-pdf-export] Inline download failed:', err);
            error = true;
        } finally {
            runningDownloads.delete(productId);
            document.dispatchEvent(new CustomEvent('nfc:flow-capacity:download-complete', {
                detail: { productId, error },
            }));
        }
    });
}

if (typeof window !== 'undefined') {
    window.NFC = window.NFC || {};
    window.NFC.generateWeirPdf = generateWeirPdf;
    window.NFC.initWeirPdfExport = initWeirPdfExport;
}
